import {
  isCompactFormField,
  type FieldOption,
} from "@product/app/workspace/channels/[channelId]/record-lens-utils";
import type { RecordImage } from "@product/components/content/record-image-viewer";
import type { WorkspaceField } from "@product/lib/mlx-workspace-client";

import {
  DATA_SET_NAME,
  SAMPLE_FIELDS,
  photoFileName,
  sampleDisplays,
  sampleOption,
  samplePhotographs,
  sampleValues,
  type SampleRecord,
} from "./fixture";
import {
  CLIENT_DISPLAYS,
  CLIENT_FIELDS,
  CLIENTS_DATA_SET_NAME,
  STATUS_OPTIONS,
  type ClientRecord,
} from "./fixture-clients";
import {
  DECISION_DISPLAYS,
  DECISION_FIELDS,
  DECISION_STATUS,
  DECISIONS_DATA_SET_NAME,
  type DecisionRecord,
} from "./fixture-decisions";

/**
 * A record as its sheet prints it, with every value already in the form the
 * application shows: text as formatFieldValue prints it, a choice as its
 * option badge, a file as its resource label. The sheet then needs no
 * formatting code, which would need @mlx/contracts values the marketing
 * bundle only has as types.
 */
export type ShotSheetCell =
  | { kind: "text"; display: string }
  | { kind: "option"; option: FieldOption }
  | { kind: "file"; title: string; href: string };

export type ShotSheetField = {
  id: string;
  key: string;
  label: string;
  required: boolean;
  /** Short fields sit two to a row (isCompactFormField). */
  compact: boolean;
  cell: ShotSheetCell;
};

export type ShotSheetRecord = {
  id: string;
  title: string;
  /** The Data Set the sheet's header names. */
  dataSetName: string;
  /** The record's image files, as RecordPhotographs lists them. */
  photographs: RecordImage[];
  fields: ShotSheetField[];
};

/** Where the product links an attached file (resourceReferenceHref). */
export function fileHref(fileId: string): string {
  return `/knowledge?file=${encodeURIComponent(fileId)}`;
}

/** A kettle sample as its record sheet prints it. */
export function ostreySheetRecord(sample: SampleRecord): ShotSheetRecord {
  const values = sampleValues(sample);
  const displays = sampleDisplays(sample);
  return {
    id: sample.id,
    title: sample.title,
    dataSetName: DATA_SET_NAME,
    photographs: samplePhotographs(sample),
    fields: SAMPLE_FIELDS.map((field) => ({
      id: field.id,
      key: field.key,
      label: field.label,
      required: field.required,
      compact: isCompactFormField(field),
      cell:
        field.type === "file"
          ? {
              kind: "file",
              title: photoFileName(String(values[field.key])),
              href: fileHref(String(values[field.key])),
            }
          : field.type === "select"
            ? { kind: "option", option: sampleOption(sample) }
            : { kind: "text", display: displays[field.key] ?? "-" },
    })),
  };
}

/**
 * A record of a data set a workflow keeps current, as its sheet prints it:
 * no photographs, each value as displayed and the one select as its option.
 */
function workflowSheetRecord(record: {
  id: string;
  title: string;
  dataSetName: string;
  fields: readonly WorkspaceField[];
  displays: Readonly<Record<string, string>>;
  option: FieldOption;
}): ShotSheetRecord {
  return {
    id: record.id,
    title: record.title,
    dataSetName: record.dataSetName,
    photographs: [],
    fields: record.fields.map((field) => ({
      id: field.id,
      key: field.key,
      label: field.label,
      required: field.required,
      compact: isCompactFormField(field),
      cell:
        field.type === "select"
          ? { kind: "option", option: record.option }
          : { kind: "text", display: record.displays[field.key] ?? "-" },
    })),
  };
}

/** A client in the landing page's workspace as its record sheet prints it. */
export function clientSheetRecord(client: ClientRecord): ShotSheetRecord {
  return workflowSheetRecord({
    id: client.id,
    title: client.name,
    dataSetName: CLIENTS_DATA_SET_NAME,
    fields: CLIENT_FIELDS,
    displays: CLIENT_DISPLAYS[client.id] ?? {},
    option: STATUS_OPTIONS.attention,
  });
}

/** An open decision in the landing page's workspace as its record sheet prints it. */
export function decisionSheetRecord(decision: DecisionRecord): ShotSheetRecord {
  return workflowSheetRecord({
    id: decision.id,
    title: decision.name,
    dataSetName: DECISIONS_DATA_SET_NAME,
    fields: DECISION_FIELDS,
    displays: DECISION_DISPLAYS[decision.id] ?? {},
    option: DECISION_STATUS.open,
  });
}
